import { AgentV2UnavailableSource } from '@/agent-v2/references/AgentV2UnavailableSource'
import { AgentV2WebSourceIcon } from '@/agent-v2/references/AgentV2WebSourceIcon'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from '@/components/link/Link'
import { AgentV2WebSourceTooltip } from '@/agent-v2/references/AgentV2WebSourceTooltip'
import { ChevronDown, ChevronUp } from '@/assets/icons'
import type { AgentV2Reference } from '@/agent-v2/store/schemas'
import { buildAgentV2ReferenceSections, type AgentV2ReferenceSectionGroup } from '@/agent-v2/references/agent-v2-reference-sections'
import {
  getAgentV2ReferenceDisplayName,
  getAgentV2ReferenceMetadataSummaryText,
  kAgentV2DocketQualifierLabel,
  kAgentV2LivingAssetKindLabels,
} from '@/agent-v2/utils/agent-v2-reference-displays'
import { canOpenAgentV2Reference, getAgentV2ExternalReferenceUrl } from '@/agent-v2/references/agent-v2-reference-opening'
import { useConversationActiveCitationId } from '@/agent-v2/hooks/useConversationActiveCitationId'
import { useAgentV2ReferenceOpensInPanel, useOpenAgentV2Reference } from '@/agent-v2/hooks/useOpenAgentV2Reference'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { CitatorOpenedIn, CitatorPanelSource, CitatorSurface } from '@/analytics/enums/citator.enums'
import { trackCitatorPanelOpened, trackCitatorReferenceGroupExpanded } from '@/analytics/utils/citator-analytics'
import { CaseStatusInfoTag } from '@/citator/components/CaseStatusInfoTag'
import { LrrContentStatusInfoTag } from '@/agent-v2/references/LrrContentStatusInfoTag'
import { getAgentV2ReferenceLrrContentStatus } from '@/agent-v2/references/agent-v2-reference-lrr-status'
import { isDisplayableLrrContentStatus } from '@/agent-v2/references/lrr-content-status'
import { CaseStatusEnum, isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { CaseStatusIndicators } from '@/citator/components/CaseStatusIndicators'
import { getCaseStatusIndicatorsLabel } from '@/citator/components/case-status-indicators'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'

/**
 * The cited/related references section under one settled assistant message,
 * the cited group is
 * collapsible and starts collapsed; the related group renders only while the
 * cited group is expanded (or on its own when nothing is cited) and has its own
 * collapsed toggle. Everything derives from the message text plus the
 * conversation reference map.
 *
 * Chips are per-support rather than per-occurrence: each reference carries one support;
 * occurrences of a
 * citation id share it.
 */
export function AgentV2MessageReferences({
  messageText,
  turnId,
  referencesByCitationId,
}: {
  messageText: string
  turnId: string
  referencesByCitationId: Record<string, AgentV2Reference>
}) {
  const [expandReferences, setExpandReferences] = useState(false)
  const [expandRelated, setExpandRelated] = useState(false)

  const { citedGroups, relatedGroups } = buildAgentV2ReferenceSections({ messageText, turnId, referencesByCitationId })
  if (citedGroups.length === 0 && relatedGroups.length === 0) return null

  const citedTitle = citedGroups.length === 1 ? 'cited reference' : 'cited references'
  const relatedTitle = relatedGroups.length === 1 ? 'related reference' : 'related references'

  const relatedGroup = relatedGroups.length > 0 && (
    <AgentV2ReferencesGroup
      title={relatedTitle}
      groups={relatedGroups}
      turnId={turnId}
      isExpanded={expandRelated}
      toggleExpand={() => setExpandRelated(!expandRelated)}
      isNested={citedGroups.length > 0}
    />
  )

  // Related sits inside the expanded cited drawer, or gets its own drawer when nothing is cited.
  if (citedGroups.length === 0) return <div className="mt-3">{relatedGroup}</div>

  return (
    <div className="mt-3">
      <AgentV2ReferencesGroup
        title={citedTitle}
        groups={citedGroups}
        turnId={turnId}
        isExpanded={expandReferences}
        toggleExpand={() => {
          const next = !expandReferences
          setExpandReferences(next)
          // Collapsing cited unmounts related; reset it so re-expanding cited
          // doesn't remount related already-open and re-fire its expanded
          // impression without any interaction.
          if (!next) setExpandRelated(false)
        }}
      >
        {relatedGroup}
      </AgentV2ReferencesGroup>
    </div>
  )
}

function AgentV2ReferencesGroup({
  title,
  groups,
  turnId,
  isExpanded,
  toggleExpand,
  isNested = false,
  children,
}: {
  title: string
  groups: AgentV2ReferenceSectionGroup[]
  turnId: string
  isExpanded: boolean
  toggleExpand: () => void
  /* A section inside another drawer rather than a drawer of its own */
  isNested?: boolean
  /* Rendered below the list while expanded */
  children?: ReactNode
}) {
  const { trackEvent } = useAnalytics()

  // True if any listed item surfaces a treatment badge (the badge renders off the
  // group's primary reference; supports in a group share one source document).
  const containsBadges = groups.some((group) => isDisplayableCaseStatus(getAgentV2ReferenceCaseStatus(group.supports[0].reference)))

  // One impression per group expansion (first open only); the boolean flags badge
  // exposure.
  const hasTrackedExpandRef = useRef(false)
  useEffect(() => {
    if (!isExpanded || hasTrackedExpandRef.current) return
    hasTrackedExpandRef.current = true
    trackCitatorReferenceGroupExpanded({ trackEvent, surface: CitatorSurface.Chat, containsBadges })
  }, [isExpanded, containsBadges, trackEvent])

  const groupCaseStatuses = groups.map((group) => getAgentV2ReferenceCaseStatus(group.supports[0].reference))

  // The dots are visual only, so the header's name spells out the treatments they stand for.
  const caseStatusIndicatorsLabel = getCaseStatusIndicatorsLabel(groupCaseStatuses)
  const treatmentSummary = caseStatusIndicatorsLabel ? `, includes ${caseStatusIndicatorsLabel}` : ''

  const Chevron = isExpanded ? ChevronUp : ChevronDown
  return (
    <div className={isNested ? 'border-t border-brand-neutral-200' : 'overflow-clip rounded border border-brand-neutral-200 bg-brand-neutral-50'}>
      <button
        aria-expanded={isExpanded}
        aria-label={`${groups.length} ${title}${treatmentSummary}`}
        className="w-full px-4 py-3 focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500 hover:bg-brand-neutral-100 flex items-center justify-between gap-x-2 text-sm font-medium text-brand-neutral-950 cursor-pointer"
        onClick={toggleExpand}
        type="button"
      >
        <span className="flex items-center gap-1.5">
          {groups.length} {title}
          <CaseStatusIndicators caseStatuses={groupCaseStatuses} />
        </span>
        <span className="flex items-center justify-center p-0.5 rounded-full border border-brand-neutral-300 bg-brand-neutral-200">
          <Chevron className="w-4 h-4 text-brand-neutral-700" />
        </span>
      </button>
      {isExpanded && (
        <div className="p-2 text-sm flex flex-col gap-2">
          {groups.map((group) => (
            <AgentV2ReferencesListItem key={`${title}_${group.sourceKey}`} group={group} turnId={turnId} />
          ))}
        </div>
      )}
      {isExpanded && children}
    </div>
  )
}

function AgentV2ReferencesListItem({ group, turnId }: { group: AgentV2ReferenceSectionGroup; turnId: string }) {
  const openReference = useOpenAgentV2Reference()
  const opensInPanel = useAgentV2ReferenceOpensInPanel()
  const activeCitationId = useConversationActiveCitationId()
  const { trackEvent } = useAnalytics()

  const primaryReference = group.supports[0].reference
  const displayName = getAgentV2ReferenceDisplayName(primaryReference)
  // The muted kind next to the display name: a living asset's kind, or the court-docket marker.
  const qualifier =
    primaryReference.source_type === 'living_asset'
      ? kAgentV2LivingAssetKindLabels[primaryReference.source.asset_kind]
      : primaryReference.source_type === 'docket'
      ? kAgentV2DocketQualifierLabel
      : null
  const canOpenViewer = canOpenAgentV2Reference(primaryReference, { opensInPanel })
  const externalUrl = getAgentV2ExternalReferenceUrl(primaryReference)
  const caseStatus = getAgentV2ReferenceCaseStatus(primaryReference)
  const lrrContentStatus = getAgentV2ReferenceLrrContentStatus(primaryReference)

  // Active when the open viewer session's citation belongs to this source document.
  const isActive = group.supports.some((support) => support.reference.citation_id === activeCitationId)

  // Open the reference directly to the citation analysis (citator) tab; an
  // overruling-risk case opens with its case-citations section expanded.
  // CaseStatusInfoTag owns CitatorBadgeClicked; this CTA only opens the citator
  // detail, and panel-opened fires for both doors (in-page panel and the
  // draft-workspace tab, which deep-links to Citation Analysis).
  const handleViewCitationAnalysis = () => {
    const opened = openReference(primaryReference, turnId, {
      defaultTab: CaseLawReferenceTab.citator,
      defaultOpenSection: caseStatus === CaseStatusEnum.overruling_risk ? RiaCitatorSection.CaseCitations : undefined,
    })
    if (opened) {
      trackCitatorPanelOpened({
        trackEvent,
        surface: CitatorSurface.Chat,
        source: CitatorPanelSource.BadgeClick,
        openedIn: opened === 'panel' ? CitatorOpenedIn.ContentViewer : CitatorOpenedIn.StandaloneViewer,
      })
    }
  }

  const titleContent = (
    <>
      [{group.footnoteNumber}] {displayName}
      {qualifier && <span className="ml-2 text-xs font-normal text-brand-neutral-600">{qualifier}</span>}
    </>
  )
  const metadataSummaryText = getAgentV2ReferenceMetadataSummaryText(primaryReference)
  const metadataContent = metadataSummaryText && <div className="text-xs text-brand-neutral-950 leading-4">{metadataSummaryText}</div>

  if (externalUrl) {
    return (
      <div className="relative flex flex-col gap-1 w-full rounded-md p-2 text-left hover:bg-brand-neutral-100">
        {/* No footnote number, per the design; copied references keep it. */}
        <AgentV2WebSourceTooltip
          source={{ url: externalUrl, title: displayName }}
          supports={primaryReference.source_type === 'web' ? group.supports : undefined}
        >
          <Link
            href={externalUrl}
            external
            className="compatible-word-break after:absolute after:inset-0 after:rounded-md focus-visible:!outline-hidden focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-brand-500"
          >
            <AgentV2WebSourceIcon url={externalUrl} className="mr-1 h-4 w-4" />
            {displayName}
          </Link>
        </AgentV2WebSourceTooltip>
        {primaryReference.source_type === 'web' ? (
          <div className="text-xs leading-4 text-brand-neutral-950 compatible-word-break">{new URL(externalUrl).hostname} • web source</div>
        ) : (
          metadataContent
        )}
      </div>
    )
  }

  if (!canOpenViewer && primaryReference.source_type === 'administrative_decision') {
    return (
      <div className="flex flex-col items-start gap-1 w-full rounded-md p-2 text-left">
        <AgentV2UnavailableSource sourceUrl={primaryReference.source.source_url}>{titleContent}</AgentV2UnavailableSource>
        {metadataContent}
      </div>
    )
  }

  return (
    // The title button stretches over the whole row; the chips and tags sit above it with their own actions.
    <div
      className={`relative flex flex-col gap-1 text-left w-full p-2 rounded-md ${
        isActive ? 'bg-brand-neutral-200' : canOpenViewer ? 'hover:bg-brand-neutral-100' : ''
      }`}
    >
      {canOpenViewer ? (
        <button
          className="text-left flex flex-col gap-1 cursor-pointer after:absolute after:inset-0 after:rounded-md focus-visible:outline-hidden focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-brand-500"
          onClick={() => openReference(primaryReference, turnId)}
          type="button"
        >
          <div className="compatible-word-break text-sm font-medium text-brand-700 underline hover:text-brand-800">{titleContent}</div>
          {metadataContent}
        </button>
      ) : (
        <div className="text-left flex flex-col gap-1">
          <div className="compatible-word-break text-sm font-medium text-brand-neutral-700" title="Source unavailable">
            {titleContent}
          </div>
          {metadataContent}
        </div>
      )}
      {group.supports.length > 1 ? (
        <div className="flex gap-x-1 text-xs leading-4 text-brand-neutral-950">
          <div className="whitespace-nowrap">Highlights: </div>
          <div className="flex flex-wrap gap-1">
            {group.supports.map((support) => {
              const supportExternalUrl = getAgentV2ExternalReferenceUrl(support.reference)
              return supportExternalUrl ? (
                <Link key={support.reference.citation_id} href={supportExternalUrl} external size="xs" className="relative whitespace-nowrap">
                  <AgentV2WebSourceIcon url={supportExternalUrl} className="mr-1 h-3.5 w-3.5" />[{support.chipLabel}]
                </Link>
              ) : canOpenAgentV2Reference(support.reference, { opensInPanel }) ? (
                <button
                  key={support.reference.citation_id}
                  className={`relative text-brand-700 hover:text-brand-800 hover:underline whitespace-nowrap ${
                    support.reference.citation_id === activeCitationId ? 'font-bold' : ''
                  }`}
                  onClick={() => openReference(support.reference, turnId)}
                  type="button"
                >
                  [{support.chipLabel}]
                </button>
              ) : (
                <span key={support.reference.citation_id} className="relative whitespace-nowrap text-brand-neutral-500" title="Source unavailable">
                  [{support.chipLabel}]
                </span>
              )
            })}
          </div>
        </div>
      ) : null}
      {isDisplayableLrrContentStatus(lrrContentStatus) && (
        <LrrContentStatusInfoTag
          status={lrrContentStatus}
          // Same door as the reference row's own title button. Withheld for an unopenable
          // source, matching the case-status CTA below.
          onViewReference={canOpenViewer ? () => openReference(primaryReference, turnId) : undefined}
          className={group.supports.length > 1 ? 'relative mt-1' : 'relative'}
        />
      )}
      {isDisplayableCaseStatus(caseStatus) && (
        // `trackImpression={false}`: per-badge impressions are replaced by the
        // group-level CitatorReferenceGroupExpanded event, but the badge click
        // (CitatorBadgeClicked) is still tracked via `surface`.
        <CaseStatusInfoTag
          status={caseStatus}
          // No CTA for an unopenable source (e.g. missing s3_path): the popover still
          // shows the treatment info, but never offers a panel it cannot open.
          onViewCitationAnalysis={canOpenViewer ? handleViewCitationAnalysis : undefined}
          className={group.supports.length > 1 ? 'relative mt-1' : 'relative'}
          surface={CitatorSurface.Chat}
          trackImpression={false}
        />
      )}
    </div>
  )
}

/** The citator treatment for a reference; only caselaw references carry one. */
function getAgentV2ReferenceCaseStatus(reference: AgentV2Reference): string | null {
  return reference.source_type === 'caselaw' ? reference.case_status ?? null : null
}
